import i18nConfig, { I18N_NS } from "Config/i18n";

type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

const BLACKLISTED_DIRNAME_CHARS_REGEX = /[\\/:*?"<>|@&%]/;
const BLACKLISTED_DIRNAME_CHARS_DISPLAY = String.raw`/\:*?"<>|@&%`;

const BLACKLISTED_FILENAME_CHARS_REGEX = /[\\/:*?"<>|%]/;
const BLACKLISTED_FILENAME_CHARS_REGEX_GLOBAL = /[\\/:*?"<>|%]/g;
const BLACKLISTED_FILENAME_CHARS_DISPLAY = String.raw`/\:*?"<>|%`;

const buildCharsMessage = (t: TranslateFn, display: string): string =>
  t("{{unallowedChars}} not allowed", {
    unallowedChars: display,
    interpolation: { escapeValue: false },
  });

/** Directory name validation (stricter – disallows @&% in addition to base set) */
export const hasBlacklistedDirnameChars = (value: string): boolean => BLACKLISTED_DIRNAME_CHARS_REGEX.test(value);

export const getBlacklistedDirnameCharsMessage = (t: TranslateFn): string =>
  buildCharsMessage(t, BLACKLISTED_DIRNAME_CHARS_DISPLAY);

export const dirnameCharValidator = (_: unknown, value: string, t: TranslateFn): Promise<void> => {
  if (hasBlacklistedDirnameChars(value)) {
    return Promise.reject(new Error(getBlacklistedDirnameCharsMessage(t)));
  }
  return Promise.resolve();
};

/** File name validation (allows @ and &) */
export const hasBlacklistedFilenameChars = (value: string): boolean => BLACKLISTED_FILENAME_CHARS_REGEX.test(value);

/** Returns deduplicated blacklisted characters found in the value, or empty string if none */
export const getFoundBlacklistedFilenameChars = (value: string): string => {
  const matches = value.match(BLACKLISTED_FILENAME_CHARS_REGEX_GLOBAL);
  if (!matches) return "";
  return [...new Set(matches)].join(" ");
};

export const getBlacklistedFilenameCharsMessage = (t: TranslateFn): string =>
  buildCharsMessage(t, BLACKLISTED_FILENAME_CHARS_DISPLAY);

export const filenameCharValidator = (_: unknown, value: string, t: TranslateFn): Promise<void> => {
  if (hasBlacklistedFilenameChars(value)) {
    return Promise.reject(new Error(getBlacklistedFilenameCharsMessage(t)));
  }
  return Promise.resolve();
};

// function ifRequired(required: boolean, value: string, cb: Function) {
//   if (required && !`${value}`.length) {
//     throw i18nConfig.t(`${I18N_NS.VALIDATION}:::This field is required`);
//   }
//   if (!required && `${value}`.length) {
//     return cb();
//   }
// }

export function ifNumber(rule: { required: boolean }, value: string) {
  if (rule.required && !`${value}`.length) {
    return Promise.reject(i18nConfig.t(`${I18N_NS.VALIDATION}:::This field is required`));
  }
  if (!rule.required && `${value}`.length) {
    return Promise.resolve();
  }
  if (typeof Number(value) !== "number") {
    return Promise.reject(i18nConfig.t(`${I18N_NS.VALIDATION}:::Must be a integer`));
  }
  return Promise.resolve();
}

// function ifIsInRange(rule: { required: boolean, min: number, max: number }, value: string, cb: Function) {
//   const valueNumber = parseInt(value, 10);
//   if (valueNumber < rule.min || valueNumber > rule.max) {
//     return cb(i18nConfig.t(
//       `${I18N_NS.VALIDATION}:::Must be between {{minNumber}} and {{maxNumber}}`,
//       { minNumber: rule.min, maxNumber: rule.max })
//     );
//   }
//   cb();
// }

/**
 * Custom validation for antd getFieldDecorator component. Validates if input value is a number between specified range.
 * @param rule - typeof ValidationRule from antd lib. Make sure to pass "max" and "min" props in validation rules.
 * rule.required is optional.
 * @param value - value of input
 * @param cb - callback function. Has to be called always in order to work properly.
 */
export function numberInRange(
  rule: { required: boolean; min: number; max: number },
  value: string,
  cb: (error: string) => void
) {
  if (rule.required && !`${value}`.length) {
    return cb(i18nConfig.t(`${I18N_NS.VALIDATION}:::This field is required`));
  }
  if (!rule.required && `${value}`.length) {
    return cb(undefined);
  }
  if (typeof Number(value) !== "number") {
    return cb(i18nConfig.t(`${I18N_NS.VALIDATION}:::Must be a integer`));
  }
  const valueNumber = parseInt(value, 10);
  if (valueNumber < rule.min || valueNumber > rule.max) {
    return cb(
      i18nConfig.t(`${I18N_NS.VALIDATION}:::Must be between {{minNumber}} and {{maxNumber}}`, {
        minNumber: rule.min,
        maxNumber: rule.max,
      })
    );
  }
  cb(undefined);
}

export function inputLengthInRange(
  rule: { required: boolean; min: number; max: number },
  value: string,
  cb: (error: string) => void
) {
  if (rule.required && !`${value}`.length) {
    return cb(i18nConfig.t(`${I18N_NS.VALIDATION}:::This field is required`));
  }
  if (!rule.required && `${value}`.length) {
    return cb(undefined);
  }
  if (value.length < rule.min || value.length > rule.max) {
    return cb(
      i18nConfig.t(`${I18N_NS.VALIDATION}:::Must be between {{minNumber}} and {{maxNumber}}`, {
        minNumber: rule.min,
        maxNumber: rule.max,
      })
    );
  }
  cb(undefined);
}
